import { Add, ChevronRight } from "@mui/icons-material";
import { Autocomplete, Box, Divider, Grid, TextField } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DepartureTimeExp, TicketType, TransportationMethod, TransportationType, ValidTextRegExp } from "../../../Constant";
import { formatDepartureDate, getBusTransportationOption, getLineOptions, getStopOptions, getTrainStationOptions, getTrainTransportationOption } from "../../../Utils";
import BusClaimApiService from "../../../adapters/xhr/BusClaimApiService";
import { getConfirmation } from "../../../components/my-confirmation-dialog/MyConfirmationDialog";
import { MyDatePicker } from "../../../components/my-date-picker/MyDatePicker";
import { MyDropdown } from "../../../components/my-dropdown/MyDropdown";
import { MyTextField } from "../../../components/my-text-field/MyTextField";
import { MyTimePicker } from "../../../components/my-time-picker/MyTimePicker";
import MyContext from "../../../contexts/MyContext";
import { BusTheme } from "../../../infrastructure/BusThemeProvider";
import { FormWrapper } from "../FormWrapper";
import { AddTransportReceiptDialog } from "../dialogs/AddTransportReceiptDialog";

export const TicketForm2 = (props) => {
  const { t, i18n } = useTranslation();
  const myContext = useContext(MyContext);
  const [addTransportReceipt, setAddTransportReceipt] = useState(false);
  const [departureStationValueErr, setDepartureStationValueErr] = useState("");
  const [departureDateValueErr, setDepartureDateValueErr] = useState("");
  const [departureTimeValueErr, setDepartureTimeValueErr] = useState(null);
  const [arrivalStationValueErr, setArrivalStationValueErr] = useState("");
  const [commentValueErr, setCommentValueErr] = useState("");
  const [commentValueRequired, setCommentValueRequired] = useState(false);
  const busTransportationTypeOption = getBusTransportationOption();
  const trainTransportationTypeOption = getTrainTransportationOption();

  const [lineValueErr, setLineValueErr] = useState("");

  const stopOptions = getStopOptions(myContext.stops);
  const lineOptions = getLineOptions(myContext.lines);
  const trainStopOptions = getTrainStationOptions(myContext.trainStations);

  const validate = async () => {
    let isValid = true;
    if (myContext.transportationMethod === TransportationMethod.BUS && !myContext.line) {
      setLineValueErr("required");
      isValid = false;
    } else {
      setLineValueErr("");
    }
    if (!myContext.departureStation) {
      setDepartureStationValueErr("required");
      isValid = false;
    } else {
      setDepartureStationValueErr("");
    }
    if (!myContext.arrivalStation) {
      setArrivalStationValueErr("required");
      isValid = false;
    } else {
      setArrivalStationValueErr("");
    }
    if (!myContext.departureDate || myContext.departureDate.trim() === "") {
      setDepartureDateValueErr("required");
      isValid = false;
    } else {
      setDepartureDateValueErr("");
    }
    if (!myContext.departureTime) {
      setDepartureTimeValueErr("required");
      isValid = false;
    } else {
      setDepartureTimeValueErr("");
    }
    if (!myContext.biljettnummerValue?.trim() && !myContext.otherComments?.trim()) {
      setCommentValueErr("required");
      isValid = false;
    } else {
      setCommentValueErr("");
    }
    if (commentValueRequired && !myContext.otherComments?.trim()) {
      setCommentValueErr("required");
      isValid = false;
    } else {
      setCommentValueErr("");
    }
    if (isValid) {
      let validTicket = false;
      let tickets = await getTicketInfoAsync();
      if (tickets) {
        if (tickets.length === 0) {
          const confirmed = await getConfirmation({
            title: t("info"),
            message: t("ticket_validity_does_not_match_departure_date"),
            theme: BusTheme,
          });
          if (confirmed) validTicket = true;
        } else validTicket = true;
      } else validTicket = true;
      if (validTicket) {
        if (!myContext.biljettnummerValue?.trim()) myContext.changeActiveSlide((myContext.activeSlide += 1));
        else {
          const duplicate = await duplicateCheck();
          if (duplicate) {
            const confirmed = await getConfirmation({
              title: t("info"),
              message: t("duplicate_claim_message"),
              theme: BusTheme,
            });
            if (confirmed) myContext.changeActiveSlide((myContext.activeSlide += 1));
          } else myContext.changeActiveSlide((myContext.activeSlide += 1));
        }
      }
    }
  };

  const duplicateCheck = async () => {
    const result = await BusClaimApiService.checkDuplicate({
      departureStationId: myContext.departureStation?.key,
      arrivalStationId: myContext.arrivalStation?.key,
      departureDate: `${formatDepartureDate(myContext.departureDate, myContext.departureTime).toISOString()}`,
      ticketNumber: myContext.biljettnummerValue?.trim(),
    });
    return result?.isSuccess && result?.data;
  };

  const getTicketInfoAsync = async () => {
    if (!myContext.biljettnummerValue || myContext.biljettnummerValue.trim() === "") return null;
    const departureDate = formatDepartureDate(myContext.departureDate, myContext.departureTime).toISOString();
    let result = await BusClaimApiService.getTicketById(myContext.biljettnummerValue.trim(), myContext.ticketType, departureDate);
    if (result?.isSuccess && result?.data) return result.data;
    else return null;
  };

  const handleChangeDepartureTime = (value) => {
    let departureTimeReg = new RegExp(DepartureTimeExp);
    if (departureTimeReg.test(value)) {
      myContext.changeDepartureTime(value);
    } else myContext.changeDepartureTime(null);
  };

  useEffect(() => {
    if (!myContext.biljettnummerValue?.trim() || (myContext.transportationType === TransportationType.OTHERS && myContext.transportReceipt.length > 0) || (myContext.transportationType === TransportationType.USEOWNCAR && myContext.numberOfKmDriven)) setCommentValueRequired(true);
    else {
      setCommentValueRequired(false);
      myContext.changeOtherComments("");
    }
  }, [myContext.biljettnummerValue, myContext.transportReceipt, myContext.transportationType, myContext.numberOfKmDriven]);

  const commentFieldLabel = () => {
    if (myContext.biljettnummerValue?.trim()) return t("other_comments");
    else return t("other_comments_without_ticket");
  };

  // single tickets for sormlandstrafiken cannot be used to travel by trains
  const transportationTypeOptions = () => {
    return myContext.ticketType === TicketType.COMMUTER ? [busTransportationTypeOption, trainTransportationTypeOption] : [busTransportationTypeOption];
  };

  return (
    <MyContext.Consumer>
      {(context) => (
        <React.Fragment>
          <FormWrapper onBack={props.slickPrev} onContinue={validate} number={`2/5`} heading={`${t("travel_info")}`}>
            <p id="required-description" className="margin bottom-0" style={{ marginTop: "0.5rem", width: "100%" }}>
              <Box component="small" color="error.main">
                *
              </Box>
              <small> {t("required_field")}</small>
            </p>
            <Autocomplete
              style={{ width: "100%", margin: "1rem 0", ...props.style }}
              options={transportationTypeOptions()}
              getOptionLabel={(option) => t(option.title)}
              value={[busTransportationTypeOption, trainTransportationTypeOption].find((option) => option.transportationMethod === context.transportationMethod)}
              onChange={(e, value) => {
                context.changeTransportationMethod(value.transportationMethod);
              }}
              disableClearable={true}
              renderInput={(params) => <TextField {...params} label={t("transportation_fardmedel")} variant="outlined" />}
            />
            {context.transportationMethod == TransportationMethod.BUS && (
              <MyDropdown
                required={true}
                helperText={t(lineValueErr)}
                onChange={(e, value) => {
                  context.changeLine(value);
                }}
                label={t("line")}
                value={context.line}
                options={lineOptions}
              />
            )}
            <MyDropdown noOptionsText={t("no_options")} required={true} helperText={t(departureStationValueErr)} onChange={(e, value) => context.changeDepartureStation(value)} label={t("from")} value={context.departureStation} options={context.transportationMethod == TransportationMethod.BUS ? stopOptions.filter((option) => option.key !== context.arrivalStation?.key) : trainStopOptions.filter((option) => option.key !== context.arrivalStation?.key)} />
            <MyDropdown noOptionsText={t("no_options")} required={true} helperText={t(arrivalStationValueErr)} onChange={(e, value) => context.changeArrivalStation(value)} label={t("to")} value={context.arrivalStation} options={context.transportationMethod == TransportationMethod.BUS ? stopOptions.filter((option) => option.key !== context.departureStation?.key) : trainStopOptions.filter((option) => option.key !== context.departureStation?.key)} />
            <MyDatePicker required={true} error={departureDateValueErr !== ""} label={t("departure_date")} value={context.departureDate} onAccept={(date) => context.changeDepartureDate(date?.toString())} helperText={t(departureDateValueErr)} maxDate={new Date()} minDate={new Date(2021, 11, 12)} placeholder={t("date_format")} cancelLabel={t("cancel")} okLabel={t("ok")} locale={i18n.language} views={["day"]} />
            {context.departureTimeOptions == null || context.departureTimeOptions.length == 0 ? <MyTimePicker required={true} onAccept={(date) => context.changeDepartureTime(date?.toString())} value={context.departureTime} label={t("departure_time")} helperText={t(departureTimeValueErr)} cancelLabel={t("cancel")} okLabel={t("ok")} /> : <MyDropdown noOptionsText={t("no_options")} required={true} helperText={t(departureTimeValueErr)} onChange={(e, time) => handleChangeDepartureTime(time !== null ? time.value : null)} label={t("departure_time")} freeSolo={new Date(context.departureDate).getDate() >= new Date().getDate()} placeholder={`HH:mm`} onFreeSolo={(e) => handleChangeDepartureTime(e.target.value)} value={context.departureTime} options={context.departureTimeOptions} />}
            <h5 style={{ width: "100%" }}>{t("do_you_have_additional_costs")}</h5>
            <Divider style={{ width: "100%" }} />
            <Grid container onClick={(e) => setAddTransportReceipt(true)} className="selector" alignItems="center" width={"100%"}>
              <Add style={{ marginRight: "1rem" }} />
              <span style={{ fontSize: "1rem" }}>{t("add_transport_expenses")}</span>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                <Box fontSize={"1rem"} component="span" color="primary.main">
                  {context.transportationType == TransportationType.OTHERS && context.transportTotalCost && `${context.transportTotalCost} ${t("kr")}`}
                  {context.transportationType == TransportationType.USEOWNCAR && context.numberOfKmDriven && `${context.numberOfKmDriven} ${t("km")}`}
                </Box>
                <ChevronRight />
              </div>
            </Grid>
            <Divider style={{ width: "100%" }} />
            {commentValueRequired && (
              <MyTextField
                inputLabelStyles={{
                  sx: {
                    whiteSpace: { xs: "wrap", sm: "break-spaces" },
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                      whiteSpace: "nowrap",
                    },
                  },
                }}
                required={commentValueRequired}
                helperText={t(commentValueErr)}
                regExp={ValidTextRegExp}
                value={context.otherComments}
                onChange={context.changeOtherComments}
                label={commentFieldLabel()}
                multiline={true}
                rows={4}
                style={{ marginBottom: "0" }}
              />
            )}
          </FormWrapper>
          <AddTransportReceiptDialog open={addTransportReceipt} onClose={(e) => setAddTransportReceipt(false)} />
        </React.Fragment>
      )}
    </MyContext.Consumer>
  );
};
